import { useEffect, useState } from "react";
import { useTheme } from "next-themes";
import { Button } from "@/components/ui/button";
import { Palette, Paintbrush } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useNavigate } from "react-router";

export const ThemeToggle = () => {
  const { theme, setTheme } = useTheme();
  const [customThemeEnabled, setCustomThemeEnabled] = useState(false);

  useEffect(() => {
    // Check if custom theme is enabled from localStorage
    const storedCustomTheme = localStorage.getItem("customThemeEnabled");
    if (storedCustomTheme) {
      setCustomThemeEnabled(storedCustomTheme === "true");
    }

    // Apply the appropriate class based on the stored value
    if (storedCustomTheme === "true") {
      document.documentElement.classList.add("wealthbee-theme");
    } else {
      document.documentElement.classList.remove("wealthbee-theme");
    }
  }, []);

  const toggleCustomTheme = (enabled: boolean) => {
    setCustomThemeEnabled(enabled);
    localStorage.setItem("customThemeEnabled", enabled ? "true" : "false");

    // Force a more reliable class addition/removal with a small delay to ensure DOM updates
    if (enabled) {
      // Remove first to avoid duplicate
      document.documentElement.classList.remove("wealthbee-theme");
      setTimeout(() => {
        document.documentElement.classList.add("wealthbee-theme");
      }, 0);
    } else {
      document.documentElement.classList.remove("wealthbee-theme");
    }
  };

  const navigate = useNavigate();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="icon">
          {customThemeEnabled ? (
            <Palette className="h-[1.2rem] w-[1.2rem] text-tertiary-100" />
          ) : (
            <Paintbrush className="h-[1.2rem] w-[1.2rem]" />
          )}
          <span className="sr-only">Toggle theme</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          onClick={() => toggleCustomTheme(true)}
          className="flex items-center gap-2"
        >
          <Palette className="h-4 w-4 text-tertiary-100" />
          <span>Daappa Theme</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => toggleCustomTheme(false)}
          className="flex items-center gap-2"
        >
          <Paintbrush className="h-4 w-4" />
          <span>Default Theme</span>
        </DropdownMenuItem>

        <div className="px-2 py-1.5 text-sm font-semibold border-t mt-1">
          Color Mode (applies to all themes)
        </div>

        <DropdownMenuItem
          onClick={() => setTheme("light")}
          className="flex items-center gap-2"
        >
          <div className="h-4 w-4 rounded-full bg-white border border-gray-200"></div>
          <span>Light Mode {theme === "light" && "✓"}</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => setTheme("dark")}
          className="flex items-center gap-2"
        >
          <div className="h-4 w-4 rounded-full bg-slate-900 border border-gray-700"></div>
          <span>Dark Mode {theme === "dark" && "✓"}</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => setTheme("system")}
          className="flex items-center gap-2"
        >
          <div className="h-4 w-4 rounded-full bg-gradient-to-r from-white to-slate-900 border border-gray-300"></div>
          <span>System Default {theme === "system" && "✓"}</span>
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={() => navigate("/app/theme")}
          className="flex items-center gap-2 border-t mt-1"
        >
          <Palette className="h-4 w-4" />
          <span>Theme Showcase</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
